import type { Dispatch, ReactNode, SetStateAction } from 'react';
import { createContext, useContext, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import type { RootState } from '../../state/store';
import { filterLocationsBySelectedDateRange } from '../../helpers/utils';
import DashboardContext from '../../helpers/contexts/dashboardContext';

interface LocationFilterContext {
  partialSelect: boolean;
  setPartialSelect: Dispatch<SetStateAction<boolean>>;
  selectedLocations: any[];
  selectedStates: string[];
  appliedFilters: number[];
  handleHierarchicalSelect: (selectedOptions: any[]) => void;
  handleSubmit: () => void;
  disableSubmit: boolean;
  companyLocations: any[];
  companyStates: any[];
  clearSelection: boolean;
  setClearSelection: Dispatch<SetStateAction<boolean>>;
  isMenuOpen: boolean;
  toggleSelectAll: boolean;
  setToggleSelectAll: Dispatch<SetStateAction<boolean>>;
  isStateFilteringAvailable: boolean;
  expandedStates: Set<string>;
  setExpandedStates: Dispatch<SetStateAction<Set<string>>>;
}

export const LocationFilterContext = createContext<LocationFilterContext | undefined>(undefined);

export const useLocationFilterConfig = () => {
  const context = useContext(LocationFilterContext);
  if (context === undefined) {
    throw new Error('useLocation must be used within a LocationFilterProvider');
  }
  return context;
};

interface Props {
  children: ReactNode;
  isMenuOpen: boolean;
  dropdownBtnRef: any;
}

export const LocationFilterProvider = ({ children, dropdownBtnRef, isMenuOpen }: Props) => {
  const [selectedLocations, setSelectedLocations] = useState<any[]>([]);
  const [selectedStates, setSelectedStates] = useState<string[]>([]);
  const [clearSelection, setClearSelection] = useState(false);
  const [toggleSelectAll, setToggleSelectAll] = useState(false);
  const [partialSelect, setPartialSelect] = useState(false);
  const [expandedStates, setExpandedStates] = useState<Set<string>>(new Set());

  const appliedFilters = useSelector((state: RootState) => state.filters.locations);
  const companyLocations = useSelector((state: RootState) => state.locations);
  const companyDetails = useSelector((state: RootState) => state.company.company_details);
  const selectedDateRange = useSelector((state: RootState) => state.filters.dates);
  const selectedTimeRange = useSelector((state: RootState) => state.filters.timeRange);
  const { applyFilter } = useContext(DashboardContext) as any;

  const locationsValidInSelectedDateRange = useMemo(() => {
    return filterLocationsBySelectedDateRange(companyLocations, selectedDateRange);
  }, [JSON.stringify(selectedDateRange), JSON.stringify(companyLocations)]);

  // Check if state filtering should be available based on company country
  const isStateFilteringAvailable = useMemo(() => {
    const country = (companyDetails as any)?.country;
    const stateSupportedCountries = ['IN', 'US', 'CA', 'AE']; // India, United States, Canada, UAE
    return stateSupportedCountries.includes(country);
  }, [(companyDetails as any)?.country]);

  // Extract distinct states from locations (only if state filtering is available)
  // Optimized for hierarchical dropdown: state > locations structure
  const companyStates = useMemo(() => {
    if (!isStateFilteringAvailable) {
      return [];
    }

    const stateMap = new Map();
    const locationsWithoutState: any[] = [];

    // Single iteration through locations for better performance
    locationsValidInSelectedDateRange.forEach((location) => {
      // Check if location has a valid state
      if (location.state && location.state.trim() !== '') {
        if (!stateMap.has(location.state)) {
          stateMap.set(location.state, {
            id: location.state,
            name: location.state,
            locations: []
          });
        }
        stateMap.get(location.state).locations.push(location);
      } else {
        // Locations without state go to "Others"
        locationsWithoutState.push(location);
      }
    });

    // Convert map to array, add location_count, and sort by name
    const statesArray = Array.from(stateMap.values())
      .map((state) => ({
        ...state,
        location_count: state.locations.length
      }))
      .sort((a, b) => a.name.localeCompare(b.name));

    // Add "Others" group if there are locations without state
    if (locationsWithoutState.length > 0) {
      statesArray.push({
        id: 'Others',
        name: 'Others',
        location_count: locationsWithoutState.length,
        locations: locationsWithoutState
      });
    }

    return statesArray;
  }, [locationsValidInSelectedDateRange, isStateFilteringAvailable]);

  const disableSubmit = useMemo(() => selectedLocations.length === 0, [selectedLocations]);

  const handleHierarchicalSelect = (selectedOptions: any[]) => {
    // Extract location IDs and state IDs from the selection
    const locationIds: number[] = [];
    const stateIdsSelected: string[] = [];
    const individualLocationIds: number[] = [];

    // First pass: collect selected states and individual locations
    selectedOptions.forEach((option: any) => {
      if (option.isState) {
        stateIdsSelected.push(option.id);
      } else {
        individualLocationIds.push(option.id);
      }
    });

    // Second pass: expand states to locations, but handle child deselection
    selectedOptions.forEach((option: any) => {
      if (option.isState) {
        const stateData = companyStates.find((s: any) => s.id === option.id);
        if (stateData) {
          // Add all locations from this state
          stateData.locations.forEach((loc: any) => {
            // Only add if not explicitly deselected (child clicked while parent selected)
            if (!option.isChild || individualLocationIds.includes(loc.id)) {
              locationIds.push(loc.id);
            }
          });
        }
      } else {
        // Individual location selected
        locationIds.push(option.id);
      }
    });

    // Check if any child was deselected while parent is selected
    // This happens when user clicks a child checkbox when parent is checked
    companyStates.forEach((state: any) => {
      if (stateIdsSelected.includes(state.id)) {
        const stateLocationIds = state.locations.map((loc: any) => loc.id);
        const selectedStateLocationIds = stateLocationIds.filter((id: number) =>
          locationIds.includes(id)
        );

        // If not all locations from this state are selected, break apart the state selection
        if (selectedStateLocationIds.length < stateLocationIds.length) {
          // Remove this state from state selection
          const stateIndex = stateIdsSelected.indexOf(state.id);
          if (stateIndex > -1) {
            stateIdsSelected.splice(stateIndex, 1);
          }
        }
      }
    });

    // Get the actual location objects
    const selectedLocs = locationsValidInSelectedDateRange.filter((loc: any) =>
      locationIds.includes(loc.id)
    );

    setSelectedLocations(selectedLocs);
    setSelectedStates(stateIdsSelected);
  };

  const handleSubmit = () => {
    if (dropdownBtnRef.current) {
      dropdownBtnRef.current.click();
    }
    applyFilter({
      type: 'location',
      data: {
        selectedLocations,
        selectedStates,
        dateRange: selectedDateRange,
        timeRange: selectedTimeRange
      }
    });
  };

  return (
    <LocationFilterContext.Provider
      value={{
        partialSelect,
        setPartialSelect,
        selectedLocations,
        selectedStates,
        appliedFilters,
        disableSubmit,
        companyLocations: locationsValidInSelectedDateRange,
        companyStates,
        clearSelection,
        isMenuOpen,
        handleSubmit,
        handleHierarchicalSelect,
        setClearSelection,
        toggleSelectAll,
        setToggleSelectAll,
        isStateFilteringAvailable,
        expandedStates,
        setExpandedStates
      }}
    >
      {children}
    </LocationFilterContext.Provider>
  );
};
